import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import defaultFormState, { requiredFields } from '@models/brandCustomProfile'

import PageContext from '@contexts/pageContext'

import * as brandCustomProfileActions from '@redux/modules/brandCustomProfile'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { BrandCustomProfileModel } from '@models/types'

const stringifyObjectKeys: (keyof BrandCustomProfileModel)[] = ['permission_data']

type CreateBrandCustomProfileParams = {
  brandCustomProfileParams: Partial<BrandCustomProfileModel>,
  dispatch: AppDispatch,
}

const createBrandCustomProfile = (params: CreateBrandCustomProfileParams) => {
  const { dispatch, brandCustomProfileParams } = params
  const { createBrandCustomProfile: createFn } = brandCustomProfileActions

  const updatedParams = mergeAndStringifyEntityObjects({}, brandCustomProfileParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams))
}

type UpdateBrandCustomProfileParams = {
  brandCustomProfile: BrandCustomProfileModel,
  brandCustomProfileParams: Partial<BrandCustomProfileModel>,
  dispatch: AppDispatch,
}

const updateBrandCustomProfile = (params: UpdateBrandCustomProfileParams) => {
  const {
    dispatch, brandCustomProfile, brandCustomProfileParams,
  } = params
  const { updateBrandCustomProfile: updateFn } = brandCustomProfileActions

  const updatedParams = mergeAndStringifyEntityObjects(
    brandCustomProfile,
    brandCustomProfileParams,
    stringifyObjectKeys,
  )

  return dispatch(updateFn(updatedParams))
}

type DeleteBrandCustomProfileParams = {
  brandCustomProfile: DeleteParams<BrandCustomProfileModel>,
  dispatch: AppDispatch,
}

const deleteBrandCustomProfile = (params: DeleteBrandCustomProfileParams) => {
  const { dispatch, brandCustomProfile } = params
  const { deleteBrandCustomProfile: deleteFn } = brandCustomProfileActions

  return dispatch(deleteFn(brandCustomProfile))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useBrandCustomProfileForm(
  brandCustomProfile: Partial<BrandCustomProfileModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const brandCustomProfileForm = useForm(
    defaultFormState,
    { entity: brandCustomProfile, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [brandCustomProfile.id, brandCustomProfile.cache_key],
  )

  return {
    ...brandCustomProfileForm,
  }
}

function useBrandCustomProfile(initEntity: Partial<BrandCustomProfileModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: brandCustomProfile }:
  { entity: BrandCustomProfileModel } = useLatestEntity(initEntity, 'brandCustomProfiles')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.brandCustomProfiles)

  return {
    brandCustomProfile,
    callbacks: {
      createOrEditBrandCustomProfileModal: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditBrandCustomProfileModal',
        payload: { brandCustomProfile },
      }),
      createBrandCustomProfile: (brandCustomProfileParams: Partial<BrandCustomProfileModel>) => (
        createBrandCustomProfile({ brandCustomProfileParams, dispatch })
      ),
      createOrEditBrandCustomProfile: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditBrandCustomProfileModal',
        payload: { brandCustomProfile },
      }),
      deleteBrandCustomProfile: () => deleteBrandCustomProfile({ dispatch, brandCustomProfile }),
      manageBrandCustomProfileUserPermissions: (
        brandCustomProfileParams: UseFormPayload,
        roleId: number,
      ) => launchModal({
        callbacks,
        modalKey: 'ManageBrandCustomProfileUserPermissionsModal',
        payload: { brandCustomProfileParams, roleId },
      }),
      updateBrandCustomProfile: (brandCustomProfileParams: Partial<BrandCustomProfileModel>) => (
        updateBrandCustomProfile({
          brandCustomProfile, brandCustomProfileParams, dispatch,
        })
      ),
    },
    creating,
    deleting,
    loading,
    updating,
  }
}

export default useBrandCustomProfile
